import { alpha, type Theme } from "@mui/material/styles";
import type { CSSProperties } from "react";
import type { AppSx } from "../types";

export interface DashboardGridComponentStyles {
  root: (fillContainer: boolean) => AppSx;
  widgetWarningAlert: AppSx;
  emptyDropZone: (dropActive: boolean, minHeight?: number) => AppSx;
  emptyDropIcon: AppSx;
  emptyDropCaption: AppSx;
  zoneOverlayContainer: (height: number) => AppSx;
  zoneBox: (rect: Record<string, string | number>) => AppSx;
  gridLayoutWrapper: AppSx;
  gridLayoutStyle: (dropActive: boolean) => CSSProperties;
  widgetCell: AppSx;
  widgetContent: AppSx;
  editToolbar: AppSx;
  decorationCell: AppSx;
  bindingIcon: AppSx;
}

export function buildDashboardGridComponentStyles(theme: Theme): DashboardGridComponentStyles {
  return {
    root: (fillContainer) => ({
      width: "100%",
      maxWidth: "100%",
      flex: fillContainer ? 1 : undefined,
      minWidth: 0,
      minHeight: fillContainer ? 0 : 360,
      height: fillContainer ? "100%" : undefined,
      overflow: fillContainer ? "hidden" : "visible",
      boxSizing: "border-box",
      borderRadius: fillContainer ? 0 : 2,
      p: fillContainer ? { xs: 0.5, md: 0.75 } : { xs: 1, md: 1.5 },
      bgcolor: fillContainer
        ? "transparent"
        : theme.palette.mode === "dark"
          ? alpha(theme.palette.common.black, 0.2)
          : "#eef1f5",
      border: fillContainer ? "none" : "1px solid",
      borderColor: "divider",
      "& .react-grid-item": { overflow: "hidden" },
      "& .dashboard-grid-cell": {
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
      },
    }),
    widgetWarningAlert: { mb: 2 },
    emptyDropZone: (dropActive, minHeight) => ({
      border: "2px dashed",
      borderColor: dropActive ? "primary.main" : alpha("#64748b", 0.35),
      borderRadius: 2,
      p: { xs: 3, md: 5 },
      mb: 2,
      textAlign: "center",
      bgcolor: dropActive
        ? alpha(theme.palette.primary.main, 0.06)
        : "background.paper",
      transition: "border-color 0.15s, background-color 0.15s",
      position: "relative",
      overflow: "hidden",
      minHeight,
    }),
    emptyDropIcon: { fontSize: 40, color: "text.disabled", mb: 1 },
    emptyDropCaption: { mt: 0.5, display: "block" },
    zoneOverlayContainer: (height) => ({
      position: "absolute",
      left: 0,
      right: 0,
      top: 88,
      height,
      pointerEvents: "none",
    }),
    zoneBox: (rect) => ({
      position: "absolute",
      ...rect,
      border: "2px dashed",
      borderColor: alpha(theme.palette.primary.main, 0.45),
      borderRadius: 1.5,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: alpha(theme.palette.primary.main, 0.04),
      boxSizing: "border-box",
    }),
    gridLayoutWrapper: { width: "100%", maxWidth: "100%", overflow: "hidden" },
    gridLayoutStyle: (dropActive) => ({
      minHeight: 120,
      borderRadius: 8,
      outline: dropActive ? "2px dashed" : undefined,
      outlineColor: dropActive ? theme.palette.primary.main : undefined,
      outlineOffset: 4,
    }),
    widgetCell: {
      position: "relative",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
    },
    widgetContent: { flex: 1, minHeight: 0, width: "100%" },
    editToolbar: {
      position: "absolute",
      top: 6,
      right: 6,
      zIndex: 5,
      bgcolor: "background.paper",
      borderRadius: 1,
      boxShadow: 1,
    },
    decorationCell: { position: "relative", overflow: "auto" },
    bindingIcon: {
      position: "absolute",
      top: 6,
      left: 6,
      zIndex: 5,
      bgcolor: "background.paper",
      borderRadius: 1,
      boxShadow: 1,
      width: 28,
      height: 28,
    },
  };
}
